import type { ComponentType } from "react";
import { useCallback, useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import DataLoadingFailed from "@entrilia/shared/components/DataLoadingFailed.tsx";
import InlineLoader from "@entrilia/shared/components/inlineLoader/InlineLoader.tsx";
import useFetch from "@entrilia/shared/hooks/useFetch.ts";
import biApi from "../../../../../api/biApi.ts";
import type { ClientSettings, DimensionDescriptor } from "../../../../../api/types/svTypes.ts";
import { useAppDispatch } from "../../sv/store/hooks/useAppDispatch.ts";
import {
  selectCurrentDataSetId,
  selectDimensions,
  selectMetaDataLoaded,
  selectMetaDataLoadingFailed,
} from "../../sv/store/selectors/metaDataSelectors.ts";
import { loadMetaData } from "../../sv/store/thunks/metaDataThunks.ts";
import { DataSetIds, DimensionNames } from "../../sv/utilities/DimensionNames.ts";
import type { BalanceSheetEntityOption } from "./utils/balanceSheetEntityFilter.ts";
import {
  getBalanceSheetEntityDictionaryFromApiResponse,
  getBalanceSheetEntityDimension,
  getBalanceSheetEntityOptions,
} from "./utils/balanceSheetEntityFilter.ts";

export interface BalanceSheetPreloadedProps {
  settings: ClientSettings;
  entityOptions: BalanceSheetEntityOption[];
}

interface WithBalanceSheetPreloadOptions {
  errorTitle: string;
}

const withBalanceSheetPreload = (
  Component: ComponentType<BalanceSheetPreloadedProps>,
  options: WithBalanceSheetPreloadOptions
) => {
  const BalanceSheetPreloadWrapper = () => {
    const dispatch = useAppDispatch();
    const dataSetId = useSelector(selectCurrentDataSetId) ?? DataSetIds.GeneralAndMemo;
    const dimensions = useSelector(selectDimensions);
    const isMetaDataLoaded = useSelector(selectMetaDataLoaded);
    const isMetaDataLoadingFailed = useSelector(selectMetaDataLoadingFailed);

    useEffect(() => {
      dispatch(loadMetaData());
    }, [dispatch]);

    const [settings, settingsError] = useFetch<ClientSettings>(biApi.getClientSettings);
    const loadEntityDictionary = useCallback(
      () => biApi.loadDimensionDictionaries(dataSetId, DimensionNames.EntityNo),
      [dataSetId]
    );
    const [entityDictionaryResponse, entityDictionaryError] = useFetch<unknown>(loadEntityDictionary);

    const entityDimension = useMemo<DimensionDescriptor | undefined>(
      () => getBalanceSheetEntityDimension(dimensions),
      [dimensions]
    );
    const entityDictionary = useMemo(
      () => getBalanceSheetEntityDictionaryFromApiResponse(entityDictionaryResponse),
      [entityDictionaryResponse]
    );
    const entityOptions = useMemo(
      () => getBalanceSheetEntityOptions(entityDimension, entityDictionary),
      [entityDimension, entityDictionary]
    );

    const hasError = isMetaDataLoadingFailed === true || !!settingsError || !!entityDictionaryError;
    const isReady = isMetaDataLoaded && settings !== undefined && entityDictionaryResponse !== undefined;

    if (hasError) {
      return <DataLoadingFailed title={options.errorTitle} />;
    }

    if (!isReady) {
      return <InlineLoader />;
    }

    return <Component settings={settings} entityOptions={entityOptions} />;
  };

  return BalanceSheetPreloadWrapper;
};

export default withBalanceSheetPreload;
